'use client';

import type { Property } from '../types/property';
import type { ProgressStep } from './MapView';

function formatPrice(price: number): string {
  return '£' + (price >= 1_000_000
    ? (price / 1_000_000).toFixed(price % 1_000_000 === 0 ? 0 : 1) + 'm'
    : (price / 1000).toFixed(0) + 'k');
}

function scoreMeta(score: number) {
  if (score >= 80) return { ring: 'ring-emerald-400', text: 'text-emerald-600', bg: 'bg-emerald-50' };
  if (score >= 60) return { ring: 'ring-amber-400',   text: 'text-amber-600',   bg: 'bg-amber-50'   };
  return                 { ring: 'ring-red-400',      text: 'text-red-600',     bg: 'bg-red-50'     };
}

function ScoreBadge({ score }: { score: number }) {
  const { ring, text, bg } = scoreMeta(score);
  return (
    <div className={`${bg} ${ring} ring-1 flex h-9 w-9 flex-shrink-0 flex-col items-center justify-center rounded-full`}>
      <span className={`${text} text-[11px] font-bold leading-none`}>{score}</span>
    </div>
  );
}

function PropertyCard({ property, isActive, onClick }: {
  property: Property;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <article
      onClick={onClick}
      className={`group flex cursor-pointer gap-3 rounded-xl border p-3 transition-all duration-150
        ${isActive
          ? 'border-blue-200 bg-blue-50 shadow-sm'
          : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm'}`}
    >
      <div className="relative h-[72px] w-24 flex-shrink-0 overflow-hidden rounded-lg bg-slate-100">
        {property.imageUrl ? (
          <img
            src={property.imageUrl}
            alt={property.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xl text-slate-300">🏠</div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate text-[13px] font-semibold text-slate-800 leading-tight">{property.title}</p>
          <ScoreBadge score={property.matchScore} />
        </div>
        <p className="mt-0.5 truncate text-[11px] text-slate-400">{property.address}</p>
        <p className="mt-1.5 text-sm font-bold text-slate-900">{formatPrice(property.price)}</p>
        <p className="mt-0.5 text-[11px] text-slate-400">
          {property.bedrooms} bed · {property.bathrooms} bath · <span className="capitalize">{property.propertyType}</span>
        </p>
      </div>
    </article>
  );
}

interface Props {
  properties: Property[];
  selectedPropertyId?: string | null;
  onSelectProperty: (id: string) => void;
  searchState: 'idle' | 'searching' | 'complete';
  progressSteps: ProgressStep[];
}

export default function PropertyPanel({ properties, selectedPropertyId, onSelectProperty, searchState, progressSteps }: Props) {
  const sorted = [...properties].sort((a, b) => b.matchScore - a.matchScore);
  const isSearching = searchState === 'searching';
  const isVisible = searchState !== 'idle';

  return (
    <aside className={`absolute inset-y-0 right-0 z-20 flex w-96 flex-col border-l border-slate-100 bg-white shadow-2xl transition-transform duration-500 ease-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}>

      {/* Header */}
      <div className="border-b border-slate-100 bg-slate-50 px-5 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">
              {searchState === 'idle' ? 'Properties nearby' : 'Search results'}
            </h2>
            <p className="mt-0.5 text-xs text-slate-400">
              {searchState === 'idle' && properties.length === 0 && 'Pan or zoom to see listings'}
              {searchState === 'idle' && properties.length > 0 && `${properties.length} listing${properties.length !== 1 ? 's' : ''} · by match score`}
              {searchState === 'searching' && `${properties.length} found so far…`}
              {searchState === 'complete' && `${properties.length} propert${properties.length !== 1 ? 'ies' : 'y'} found`}
            </p>
          </div>
          {properties.length > 0 && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold text-white ${isSearching ? 'bg-blue-400' : 'bg-blue-600'}`}>
              {properties.length}
            </span>
          )}
        </div>

        {/* Progress steps */}
        {progressSteps.length > 0 && searchState !== 'idle' && (
          <div className="mt-3 flex flex-col gap-1.5">
            {progressSteps.slice(-3).map((step, i) => {
              const globalIndex = progressSteps.length - Math.min(progressSteps.length, 3) + i;
              const isLast = globalIndex === progressSteps.length - 1;
              const isDone = !isLast || searchState === 'complete';
              return (
                <div key={i} className="flex items-center gap-2">
                  {isDone ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 flex-shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-blue-200 border-t-blue-500" />
                  )}
                  <span className={`text-[11px] ${isDone ? 'text-slate-400' : 'font-medium text-slate-600'}`}>
                    {step.message}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {sorted.length === 0 && searchState === 'idle' && (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-9 w-9 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <p className="max-w-[160px] text-sm leading-relaxed">Move the map to discover properties in this area</p>
          </div>
        )}

        {sorted.length === 0 && searchState === 'searching' && (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center text-slate-400">
            <span className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
            <p className="text-sm">Searching for properties…</p>
          </div>
        )}

        {sorted.length > 0 && (
          <ul className="flex flex-col gap-2.5">
            {sorted.map((p) => (
              <li key={p.id}>
                <PropertyCard
                  property={p}
                  isActive={p.id === selectedPropertyId}
                  onClick={() => onSelectProperty(p.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
